import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Home from './pages/Home';
import ClusterHome from './pages/ClusterHome';
import HospitalHome from './pages/HospitalHome';
import UserHome from './pages/UserHome';
import ViewerHome from './pages/ViewerHome';
import Account from './pages/Account';
import Hospitals from './pages/Hospitals';
import Specialties from './pages/Specialties';
import SpecialtyInputs from './pages/SpecialtyInputs';
import UserInputs from './pages/UserInputs';
import Users from './pages/Users';
import Updates from './pages/Updates';
import SystemStats from './pages/SystemStats';
import Referrals from './pages/Referrals';
import Header from './components/Header';
import Footer from './components/Footer';
import LoadingSpinner from './components/LoadingSpinner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import { apiPost } from './lib/data';
import IPRestriction from './components/IPRestriction';

function ProtectedLayout({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner fullscreen message="جاري تحضير لوحة التحكم..." />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function HomeRouter() {
  const { user } = useAuth();

  if (!user) return <Landing />;

  if (user.role === 'admin' || user.role === 'moderator' || user.role === 'rc_view') {
    return <Home />;
  }
  if (user.role === 'viewer') {
    return <ViewerHome />;
  }
  if (user.role === 'user') {
    if (user.access_level === 'hospital') {
      return <HospitalHome />;
    }
    return <ClusterHome />;
  }
  return <UserHome />;
}

function PublicOnly({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <LoadingSpinner fullscreen message="جاري التحقق من الجلسة..." />;
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function RootRoute() {
  const { user, isLoading } = useAuth();
  if (isLoading) return <LoadingSpinner fullscreen message="جاري التحقق من الجلسة..." />;
  if (!user) return <Landing />;
  return (
    <ProtectedLayout>
      <HomeRouter />
    </ProtectedLayout>
  );
}

function Protected({ children }: { children: React.ReactNode }) {
  return <ProtectedLayout>{children}</ProtectedLayout>;
}

// Closing the site is a property of a deployment, not of the source:
//
//     VITE_MAINTENANCE_MODE=notice ./scripts/deploy.sh production
//     VITE_MAINTENANCE_MODE=fault  ./scripts/deploy.sh production
//     ./scripts/deploy.sh production                 <- open again
//
// An ordinary deploy reopens the site, so there is no edit anyone has to
// remember to revert. deploy.sh refuses a value it does not recognise rather
// than quietly shipping the open application.
//
//   notice  planned work, stated plainly ("1" means this too, as it used to)
//   fault   the site presents itself as broken: a database error and a line
//           telling the visitor to contact the administrator
const OFFLINE_MODE = import.meta.env.VITE_MAINTENANCE_MODE ?? '';
const OFFLINE = OFFLINE_MODE === 'fault' || OFFLINE_MODE === 'notice' || OFFLINE_MODE === '1';

// Both screens do this. Whoever was signed in when the build went out stops
// being signed in the next time they load a page.
function useSignOutOnMount() {
  React.useEffect(() => {
    apiPost('/api/auth/sign-out').catch(() => {});
  }, []);
}

function SystemFaultScreen() {
  useSignOutOnMount();

  // Rendered at load so the card reads like something that just happened.
  const stamp = new Intl.DateTimeFormat('en-GB', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).format(new Date()).replace(',', '');

  return (
    <div dir="rtl" className="min-h-screen flex items-center justify-center bg-gray-100 px-6 py-10">
      <div className="bg-white rounded-xl shadow-lg border-t-4 border-red-600 p-8 max-w-md w-full text-center">
        <div className="mx-auto mb-5 h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
          <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>

        <h2 className="text-xl font-bold text-gray-800 mb-3">خطأ في الاتصال بقاعدة البيانات</h2>

        <p className="text-gray-600 leading-relaxed">
          تعذر الاتصال بقاعدة البيانات، ولا يمكن عرض بيانات المنصة في الوقت الحالي.
        </p>
        <p className="mt-2 text-gray-600 leading-relaxed">
          الرجاء المحاولة لاحقاً أو التواصل مع مسؤول النظام.
        </p>

        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-6 inline-flex items-center justify-center rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        >
          إعادة المحاولة
        </button>

        <div className="mt-6 border-t border-gray-100 pt-4 space-y-1 text-xs text-gray-400" dir="ltr">
          <p className="font-mono">DB_CONN_TIMEOUT_503</p>
          <p className="font-mono">{stamp}</p>
        </div>
      </div>
    </div>
  );
}

function MigrationNotice() {
  useSignOutOnMount();


  return (
    <div dir="rtl" className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-teal-50/40 px-6">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="mx-auto mb-5 h-16 w-16 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 text-white flex items-center justify-center shadow-lg shadow-teal-500/30">
          <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M5 6.75A1.75 1.75 0 016.75 5h10.5A1.75 1.75 0 0119 6.75v2.5A1.75 1.75 0 0117.25 11H6.75A1.75 1.75 0 015 9.25v-2.5zM5 14.75A1.75 1.75 0 016.75 13h10.5A1.75 1.75 0 0119 14.75v2.5A1.75 1.75 0 0117.25 19H6.75A1.75 1.75 0 015 17.25v-2.5z" />
            <path strokeLinecap="round" strokeWidth={1.8} d="M8.5 8h.01M8.5 16h.01" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-slate-900 mb-3">جاري نقل الموقع إلى الخادم الجديد</h2>
        <p className="text-slate-600 leading-relaxed">
          تم تحديث البنية التقنية للمنصة، ونعمل حالياً على إكمال عملية النقل.
        </p>
        <p className="mt-2 text-slate-600 leading-relaxed">
          سيعود الموقع للعمل قريباً. نعتذر عن أي إزعاج.
        </p>
        <p className="mt-6 font-mono text-xs tracking-wide text-slate-400">NEW SERVER · rc-saudi VPS</p>
      </div>
    </div>
  );
}

function AppContent() {
  const { transitionMessage } = useAuth();

  return (
    <>
    {transitionMessage && <LoadingSpinner fullscreen message={transitionMessage} />}
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route path="/account" element={<Protected><Account /></Protected>} />
      <Route path="/hospitals" element={<Protected><Hospitals /></Protected>} />
      <Route path="/specialties" element={<Protected><Specialties /></Protected>} />
      <Route path="/inputs" element={<Protected><SpecialtyInputs /></Protected>} />
      <Route path="/user-inputs" element={<Protected><UserInputs /></Protected>} />
      <Route path="/users" element={<Protected><Users /></Protected>} />
      <Route path="/updates" element={<Protected><Updates /></Protected>} />
      <Route path="/system-stats" element={<Protected><SystemStats /></Protected>} />
      <Route path="/referrals" element={<Protected><Referrals /></Protected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  // Ahead of the providers and the country gate: whoever the visitor is and
  // wherever they are connecting from, this is all they get. Nothing else
  // mounts, so no route exists to sign in through and nothing reaches the
  // database.
  if (OFFLINE) {
    return OFFLINE_MODE === 'fault' ? <SystemFaultScreen /> : <MigrationNotice />;
  }

  return (
    <DataProvider>
      <AuthProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <IPRestriction>
            <Toaster position="top-center" />
            <AppContent />
          </IPRestriction>
        </Router>
      </AuthProvider>
    </DataProvider>
  );
}

export default App;
